import axios from 'axios';
import { alpacaService } from './alpaca';

export interface BacktestParams {
  symbol: string;
  startDate: string;
  endDate: string;
  interval: string;
  initialCapital: number;
  code: string;
}

export interface BacktestResult {
  equity: { date: string; value: number }[];
  trades: {
    date: string;
    type: string;
    price: number;
    qty: number;
    value: number;
  }[];
  metrics: {
    totalReturn: number;
    annualizedReturn: number;
    sharpeRatio: number;
    maxDrawdown: number;
    winRate: number;
    profitFactor: number;
  };
  chart: {
    dates: string[];
    prices: number[];
    buySignals: { date: string; price: number }[];
    sellSignals: { date: string; price: number }[];
  };
}

class BacktestService {
  // Server URL for Python execution
  private backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  /**
   * Runs a backtest on the server using the provided strategy code
   */
  async runBacktest(params: BacktestParams): Promise<BacktestResult> {
    try {
      const response = await axios.post(`${this.backendUrl}/api/backtest/run`, params);
      return response.data;
    } catch (error) {
      console.error('Error running backtest:', error);
      throw new Error('Failed to run backtest');
    }
  }

  /**
   * Gets historical price data for a symbol from Alpaca
   */
  async getHistoricalData(symbol: string, timeframe: string = '1D', limit: number = 1000): Promise<any> {
    try {
      const bars = await alpacaService.getBars(symbol, timeframe, limit);
      return bars;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw new Error('Failed to fetch historical data');
    }
  }

  /**
   * Executes a quick local backtest for initial visualization
   * This is a simplified version that runs in the browser for instant feedback
   */
  async runQuickBacktest(code: string, symbol: string): Promise<any> {
    try {
      // Fetch historical data
      const bars = await this.getHistoricalData(symbol);
      
      // For the quick backtest, we'll send this to a simplified endpoint
      const response = await axios.post(`${this.backendUrl}/api/backtest/quick`, {
        code,
        data: bars
      });
      
      return response.data;
    } catch (error) {
      console.error('Error running quick backtest:', error);
      throw new Error('Failed to run quick backtest');
    }
  }

  /**
   * Deploys a strategy to Alpaca
   */
  async deployToAlpaca(strategyId: string, userId: string): Promise<any> {
    try {
      const response = await axios.post(`${this.backendUrl}/api/strategy/deploy`, {
        strategyId,
        userId,
        platform: 'alpaca'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error deploying strategy to Alpaca:', error);
      throw new Error('Failed to deploy strategy');
    }
  }

  /**
   * Gets the deployment status of a strategy
   */
  async getDeploymentStatus(deploymentId: string): Promise<any> {
    try {
      const response = await axios.get(`${this.backendUrl}/api/strategy/status/${deploymentId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting deployment status:', error);
      throw new Error('Failed to get deployment status');
    }
  }
}

export const backtestService = new BacktestService();
